import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Crown, Sparkles, Check, Loader2, ChevronDown, ChevronUp } from 'lucide-react';
import { toast } from '../../hooks/use-toast';
import { adminPlans } from '../../services/plansApi';

const PLAN_COLORS = {
  start: 'border-slate-400 bg-slate-50',
  grow: 'border-cyan-500 bg-cyan-50',
  drivepro: 'border-fuchsia-500 bg-fuchsia-50',
};

const PLAN_BADGE = {
  start: 'bg-slate-200 text-slate-800 border-slate-300',
  grow: 'bg-cyan-100 text-cyan-800 border-cyan-300',
  drivepro: 'bg-fuchsia-100 text-fuchsia-800 border-fuchsia-300',
};

const DealerPlansManager = () => {
  const [catalog, setCatalog] = useState(null);
  const [dealers, setDealers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState(null);
  const [expandedId, setExpandedId] = useState(null);

  const load = async () => {
    try {
      const [c, d] = await Promise.all([adminPlans.catalog(), adminPlans.listDealers()]);
      setCatalog(c);
      setDealers(d.dealers || []);
    } catch (e) {
      toast({ title: 'Failed to load plans', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const handlePlanChange = async (dealerId, plan) => {
    setSavingId(dealerId);
    try {
      const res = await adminPlans.setPlan(dealerId, plan);
      setDealers((prev) =>
        prev.map((d) => (d.id === dealerId ? { ...d, plan: res.plan, resolved: res.resolved } : d)),
      );
      toast({ title: `Plan updated to ${plan}` });
    } catch (e) {
      toast({ title: 'Failed to update plan', variant: 'destructive' });
    } finally {
      setSavingId(null);
    }
  };

  const handleFeatureToggle = async (dealer, flag, nextValue) => {
    setSavingId(dealer.id);
    try {
      // nextValue can be true / false / null (= clear override)
      const features = { [flag]: nextValue };
      const res = await adminPlans.setFeatures(dealer.id, features);
      setDealers((prev) =>
        prev.map((d) =>
          d.id === dealer.id ? { ...d, overrides: res.overrides, resolved: res.resolved } : d,
        ),
      );
      toast({
        title: nextValue === null
          ? `${flag}: reset to plan default`
          : `${flag}: ${nextValue ? 'enabled' : 'disabled'}`,
      });
    } catch (e) {
      toast({ title: 'Failed to update feature', variant: 'destructive' });
    } finally {
      setSavingId(null);
    }
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="py-12 text-center text-gray-500">
          <Loader2 className="w-6 h-6 mx-auto animate-spin" />
          <p className="mt-2 text-sm">Loading dealers…</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card data-testid="admin-dealer-plans">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Crown className="w-5 h-5 text-fuchsia-600" />
          Dealer Plans & Feature Flags
        </CardTitle>
        <p className="text-sm text-gray-500 mt-1">
          Assign a plan and selectively toggle individual features per dealer.
          Feature overrides take priority over plan defaults — useful for custom add-on packages.
        </p>
      </CardHeader>
      <CardContent>
        {/* Plan catalog */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-6">
          {catalog?.plans?.map((p) => (
            <div
              key={p.id}
              className={`p-4 rounded-xl border-2 ${PLAN_COLORS[p.id] || 'border-gray-300 bg-gray-50'}`}
            >
              <div className="flex items-center justify-between">
                <div className="font-bold text-gray-900">{p.label}</div>
                <span className={`text-[10px] px-2 py-0.5 rounded-full border uppercase font-semibold tracking-wider ${PLAN_BADGE[p.id] || ''}`}>
                  {p.positioning}
                </span>
              </div>
              <p className="text-xs text-gray-600 mt-1.5 leading-relaxed">{p.tagline}</p>
              <p className="text-[11px] text-gray-500 mt-2">
                {Object.values(p.features).filter(Boolean).length} of {catalog.all_flags.length} features enabled
              </p>
            </div>
          ))}
        </div>

        {/* Dealer list */}
        <div className="space-y-3">
          <h3 className="text-sm font-semibold text-gray-600 uppercase tracking-wider mb-2">
            Dealers ({dealers.length})
          </h3>
          {dealers.map((d) => {
            const isExpanded = expandedId === d.id;
            const isSaving = savingId === d.id;
            return (
              <div
                key={d.id}
                data-testid={`dealer-row-${d.id}`}
                className="bg-white border border-gray-200 rounded-lg overflow-hidden"
              >
                <div className="p-4 flex items-center gap-4 flex-wrap">
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-gray-900 truncate">{d.name}</div>
                    <div className="text-xs text-gray-500 truncate">{d.email} · {d.slug}</div>
                  </div>

                  {/* Plan picker */}
                  <div className="flex items-center gap-1">
                    {catalog?.plans?.map((p) => (
                      <button
                        key={p.id}
                        onClick={() => d.plan !== p.id && handlePlanChange(d.id, p.id)}
                        disabled={isSaving}
                        data-testid={`set-plan-${d.id}-${p.id}`}
                        className={`px-3 py-1.5 text-xs font-bold uppercase tracking-wider border-2 rounded-full transition-colors ${
                          d.plan === p.id
                            ? PLAN_BADGE[p.id] + ' border-current'
                            : 'border-gray-200 text-gray-500 hover:border-gray-400 hover:text-gray-700 bg-white'
                        }`}
                      >
                        {p.label}
                      </button>
                    ))}
                  </div>

                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setExpandedId(isExpanded ? null : d.id)}
                    data-testid={`toggle-features-${d.id}`}
                  >
                    {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                    Features
                  </Button>
                </div>

                {isExpanded && (
                  <div className="border-t bg-gray-50 p-4">
                    <div className="text-xs text-gray-500 mb-3">
                      Click to toggle. <span className="font-semibold">Override</span> = explicit on/off for this dealer
                      (takes priority over plan). <span className="font-semibold">Default</span> = inherits from plan.
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                      {catalog?.all_flags.map((flag) => {
                        const overrideValue = d.overrides?.[flag];
                        const resolved = d.resolved?.[flag];
                        const isOverride = overrideValue !== undefined;
                        return (
                          <div
                            key={flag}
                            data-testid={`feature-row-${d.id}-${flag}`}
                            className="flex items-center justify-between bg-white border border-gray-200 rounded-md px-3 py-2"
                          >
                            <div className="text-sm">
                              <div className="font-mono text-xs text-gray-800">{flag}</div>
                              <div className="text-[10px] text-gray-400">
                                {isOverride ? (
                                  <span className="text-blue-700 font-semibold">Override: {String(overrideValue)}</span>
                                ) : (
                                  <span>Default: {String(resolved)} (from plan)</span>
                                )}
                              </div>
                            </div>
                            <div className="flex items-center gap-1">
                              <button
                                disabled={isSaving}
                                onClick={() => handleFeatureToggle(d, flag, true)}
                                className={`text-[10px] font-bold uppercase px-2 py-1 rounded ${
                                  overrideValue === true
                                    ? 'bg-green-600 text-white'
                                    : 'bg-gray-100 text-gray-600 hover:bg-green-100'
                                }`}
                                data-testid={`feature-on-${d.id}-${flag}`}
                              >
                                ON
                              </button>
                              <button
                                disabled={isSaving}
                                onClick={() => handleFeatureToggle(d, flag, false)}
                                className={`text-[10px] font-bold uppercase px-2 py-1 rounded ${
                                  overrideValue === false
                                    ? 'bg-red-600 text-white'
                                    : 'bg-gray-100 text-gray-600 hover:bg-red-100'
                                }`}
                                data-testid={`feature-off-${d.id}-${flag}`}
                              >
                                OFF
                              </button>
                              {isOverride && (
                                <button
                                  disabled={isSaving}
                                  onClick={() => handleFeatureToggle(d, flag, null)}
                                  className="text-[10px] text-gray-500 hover:text-gray-700 px-1.5 py-1 underline"
                                  data-testid={`feature-clear-${d.id}-${flag}`}
                                  title="Clear override (use plan default)"
                                >
                                  reset
                                </button>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
};

export default DealerPlansManager;
